import { ExclamationCircleOutlined } from '@ant-design/icons'
import { BillingMethod } from '@components/business/billingMethod/BillingMethod'
import { initialQuery } from '@supplement-bacon/sb-table-filters'
import { SBRM, SBRMAction, useSBRM } from '@supplement-bacon/sbrm-react'
import { capitalizeFirstLetter } from '@utils/string'
import {
  Col,
  Collapse,
  CollapseProps,
  Descriptions,
  Flex,
  Row,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd'
import { useEffect } from 'react'
import { Localizable } from '../../../components/localizable/Localizable'
import Page from '../../../components/Page/Page'
import { getDays, selectAllDays } from '../../../store/business/DayReducer'
import {
  getPolicies,
  selectPolicies,
} from '../../../store/business/PolicyReducer'
import { selectAllShifts } from '../../../store/business/ShiftReducer'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import { Entity } from '../../../utils/Entities'
import { Permission } from '../../../utils/permissions'

const { Text } = Typography

const PoliciesPage = () => {
  const dispatch = useAppDispatch()
  const policies = useAppSelector(selectPolicies())
  const days = useAppSelector(selectAllDays())
  const shifts = useAppSelector(selectAllShifts())
  const { isOpen } = useSBRM()

  useEffect(() => {
    dispatch(getDays(initialQuery))
    dispatch(getPolicies(initialQuery))
  }, [])

  useEffect(() => {
    if (isOpen) return
    dispatch(getDays(initialQuery))
    dispatch(getPolicies(initialQuery))
  }, [isOpen])

  const items: CollapseProps['items'] = policies.map((policy) => {
    const columns = [
      {
        title: 'Day',
        dataIndex: 'day',
        key: 'day',
      },
      ...shifts.flatMap((shift) => [
        {
          title: capitalizeFirstLetter(shift.name),
          dataIndex: shift.name,
          key: shift.name,
          render: (value: boolean) =>
            value ? <Tag color="green">✓</Tag> : <Tag color="red">✗</Tag>,
        },
        {
          title: `${capitalizeFirstLetter(shift.name)} billing method`,
          key: `${shift.name}BillingMethod`,
          dataIndex: `${shift.name}BillingMethod`,

          render: (_: any, record: any) => (
            <Space>
              {record[`${shift.name}BillingMethod`] ? (
                <Text color="blue">{record[`${shift.name}BillingMethod`]}</Text>
              ) : (
                <Tag color="default">-</Tag>
              )}
              {record[`${shift.name}Note`] && (
                <Tooltip title={record[`${shift.name}Note`]}>
                  <ExclamationCircleOutlined style={{ color: '#1890ff' }} />
                </Tooltip>
              )}
            </Space>
          ),
        },
      ]),
    ]

    const dataSource = days.map((day) => {
      const record: any = {
        key: day.id,
        day: day.name,
      }

      shifts.forEach((shift) => {
        const match = policy.policyShifts?.find(
          (s) => s.day === day.id && s.shift === shift.id
        )

        record[shift.name] = !!match
        if (match) {
          record[`${shift.name}BillingMethod`] = match.billingMethod && (
            <BillingMethod.Avatar id={match?.billingMethod} />
          )

          record[`${shift.name}Note`] = match.note
        }
      })

      return record
    })

    return {
      key: policy.id.toString(),
      label: (
        <Space className="w-full space-between">
          <Text>{policy.name}</Text>
          <SBRM.Actions
            actions={[SBRMAction.Update, SBRMAction.Delete]}
            entity={Entity.policy}
            entityId={policy.id}
          />
        </Space>
      ),
      children: (
        <Row gutter={[32, 32]}>
          <Col span={24}>
            <Space orientation="vertical" size="middle" className="w-full">
              {(policy.en != undefined || policy.fr != undefined) && (
                <Flex vertical gap={8}>
                  <Localizable.Render
                    item={policy}
                    render={(localizedItem, lang) => (
                      <Descriptions column={1} size="small">
                        <Descriptions.Item>
                          {lang.flag} Description : {localizedItem.description}
                        </Descriptions.Item>
                      </Descriptions>
                    )}
                  />
                </Flex>
              )}

              <Table
                columns={columns}
                dataSource={dataSource}
                pagination={false}
                size="small"
              />
            </Space>
          </Col>
        </Row>
      ),
    }
  })

  return (
    <Page
      permissions={[Permission.manageBusiness]}
      pageTitle={{
        title: 'Policies',
        level: 4,
        toolbar: (
          <SBRM.Actions actions={[SBRMAction.Create]} entity={Entity.policy} />
        ),
      }}
    >
      <Collapse className="policy-collapse" items={items} />
    </Page>
  )
}

export default PoliciesPage
